import axios from 'axios';
import type { DeliveryProofAttachment } from '@/features/delivery-proof/types/delivery-proof.types';

/** Taille maximale d'une pièce jointe, alignée sur l'API */
export const DELIVERY_PROOF_MAX_FILE_SIZE = 10 * 1024 * 1024;

/** Nombre maximal de pages d'un PDF joint, contrôlé par l'API à l'upload */
export const DELIVERY_PROOF_MAX_FILE_PAGES = 5;

/**
 * Pages d'une pièce jointe : `filePages` si elle a été convertie page par
 * page, sinon l'image unique de `fileUrl` (photos, attestations antérieures).
 * Même règle que l'API pour les annexes du PDF.
 */
export const getAttachmentPages = (attachment: {
  fileUrl?: string;
  filePages?: string[];
}): string[] => {
  if (attachment.filePages?.length) return attachment.filePages;
  return attachment.fileUrl ? [attachment.fileUrl] : [];
};

/**
 * Pièce jointe d'un élément ou d'une info véhicule, ou `undefined` s'il n'en a pas
 */
export const toAttachment = (item: {
  fileUrl?: string;
  filePages?: string[];
  originalFileUrl?: string;
}): DeliveryProofAttachment | undefined =>
  item.fileUrl
    ? { fileUrl: item.fileUrl, filePages: item.filePages, originalFileUrl: item.originalFileUrl }
    : undefined;

/** Formats acceptés par l'API (FileTypeValidator du controller) */
const ACCEPTED_MIME_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];

/** Fichier refusé avant l'envoi, dont le message s'affiche tel quel */
export class AttachmentRejectedError extends Error {}

/**
 * Refuse avant l'envoi un fichier que l'API refuserait forcément : pas
 * d'upload de plusieurs Mo pour rien, et un message en français là où l'API
 * renverrait celui, technique, de son validateur de format
 */
export const assertAttachmentAcceptable = (file: File): void => {
  // Le glisser-déposer ignore l'attribut `accept` de l'input.
  if (!ACCEPTED_MIME_TYPES.includes(file.type)) {
    throw new AttachmentRejectedError('Format non pris en charge : envoyez un PDF, un JPEG ou un PNG.');
  }

  // Même borne que le MaxFileSizeValidator de l'API (taille strictement inférieure).
  if (file.size >= DELIVERY_PROOF_MAX_FILE_SIZE) {
    const sizeMb = (file.size / 1024 / 1024).toLocaleString('fr-FR', { maximumFractionDigits: 1 });
    throw new AttachmentRejectedError(
      `Fichier trop lourd (${sizeMb} Mo) : ${DELIVERY_PROOF_MAX_FILE_SIZE / 1024 / 1024} Mo maximum.`,
    );
  }
};

/**
 * Message à afficher après un échec d'upload : celui de l'API quand elle en
 * donne un (PDF de plus de 5 pages, illisible, trop lourd…)
 */
export const getAttachmentErrorMessage = (error: unknown): string => {
  if (error instanceof AttachmentRejectedError) return error.message;

  if (axios.isAxiosError(error)) {
    if (error.code === 'ECONNABORTED') {
      return 'L\'envoi a pris trop de temps. Vérifiez votre connexion et réessayez.';
    }
    const message = (error.response?.data as { message?: unknown } | undefined)?.message;
    if (typeof message === 'string' && message.trim()) return message;
    if (Array.isArray(message) && message.length > 0) return message.join(' ');
  }

  return 'Erreur lors de l\'upload du fichier';
};
